// Considered: inlining the fields directly in ContextViewEditorOrganism, as the
// base-config card (C) does — rejected because this body's contract is not
// "render some fields" but *which key emissions mean inherit vs. explicit*:
// selecting Inherit must DELETE the key from the level's override object rather
// than write a sentinel, or resolveLevelConfig can never fall back to base.
// Three acceptance tests assert exactly that emission contract, and it is
// materially testable only against a component with a pure props API. Note the
// resolution *semantics* live in resolveLevelConfig (TreeMap/defaults.ts), not
// here — this molecule owns only the emission shape. See spec Design System
// Reuse Audit.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import type { LevelDisplayOverride } from 'types/contextView';
import './LevelOverrideEditorMolecule.scss';

export interface LevelOverridePropertyOption {
  label: string;
  value: string;
}

type FlagValue = 'inherit' | 'on' | 'off';
type PropertiesModeValue = 'inherit' | 'custom';

export interface LevelOverrideEditorMoleculeProps {
  /** The level's current override entry. Absent fields mean "inherit". */
  override: LevelDisplayOverride;
  propertyOptions?: LevelOverridePropertyOption[];
  /** Field set to `undefined` means "back to inherit" — the field is deleted, not stored as `undefined`. */
  onFieldChange: <K extends keyof LevelDisplayOverride>(
    field: K,
    value: LevelDisplayOverride[K] | undefined
  ) => void;
  onResetAll: () => void;
  disabled?: boolean;
  className?: string;
}

const LevelOverrideEditorMolecule: React.FC<LevelOverrideEditorMoleculeProps> = ({
  override,
  propertyOptions = [],
  onFieldChange,
  onResetAll,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [addingProperty, setAddingProperty] = useState(false);
  const [newProperty, setNewProperty] = useState('');

  const densityOptions = [
    { value: 'inherit' as const, label: t('context.viewEditor.levelOverrides.inherit'), disabled },
    { value: 'minimal' as const, label: t('context.viewEditor.cardDensityOptions.minimal'), disabled },
    { value: 'standard' as const, label: t('context.viewEditor.cardDensityOptions.standard'), disabled },
    { value: 'expanded' as const, label: t('context.viewEditor.cardDensityOptions.expanded'), disabled },
  ];

  const flagOptions: Array<{ value: FlagValue; label: string; disabled: boolean }> = [
    { value: 'inherit', label: t('context.viewEditor.levelOverrides.inherit'), disabled },
    { value: 'on', label: t('context.viewEditor.levelOverrides.on'), disabled },
    { value: 'off', label: t('context.viewEditor.levelOverrides.off'), disabled },
  ];

  const propertiesModeOptions: Array<{ value: PropertiesModeValue; label: string; disabled: boolean }> = [
    { value: 'inherit', label: t('context.viewEditor.levelOverrides.inherit'), disabled },
    { value: 'custom', label: t('context.viewEditor.levelOverrides.custom'), disabled },
  ];

  const visibleProperties = override.visibleProperties;
  const hasCustomProperties = visibleProperties !== undefined;
  const overrideCount = Object.keys(override).length;

  const remainingPropertyOptions = propertyOptions.filter(
    (o) => !(visibleProperties ?? []).includes(o.value)
  );

  const commitNewProperty = () => {
    const trimmed = newProperty.trim();
    if (trimmed && !(visibleProperties ?? []).includes(trimmed)) {
      onFieldChange('visibleProperties', [...(visibleProperties ?? []), trimmed]);
    }
    setNewProperty('');
    setAddingProperty(false);
  };

  const flagRow = (
    field: 'showStatus' | 'showFlow' | 'showAvatar',
    label: string
  ) => {
    const current = override[field];
    // Three states, not two: unset means 'inherit', which is distinct from an
    // explicit false.
    let flagValue: FlagValue;
    if (current === undefined) {
      flagValue = 'inherit';
    } else {
      flagValue = current ? 'on' : 'off';
    }
    return (
      <div className="level-override-editor__row">
        <span className="level-override-editor__row-label">{label}</span>
        <SegmentedControlAtom
          mode="single"
          size="small"
          options={flagOptions}
          value={flagValue}
          onChange={(v) =>
            onFieldChange(field, v === 'inherit' ? undefined : v === 'on')
          }
          ariaLabel={label}
        />
      </div>
    );
  };

  return (
    <div className={`level-override-editor ${className}`.trim()}>
      <div className="level-override-editor__header">
        <span className="level-override-editor__count">
          {overrideCount > 0
            ? t('context.viewEditor.levelOverrides.overrideCount', { count: overrideCount })
            : t('context.viewEditor.levelOverrides.noOverrides')}
        </span>
        <ButtonAtom
          label={t('context.viewEditor.levelOverrides.resetAll')}
          variant="ghost"
          size="small"
          onClick={onResetAll}
          disabled={disabled || overrideCount === 0}
        />
      </div>

      <div className="level-override-editor__row">
        <span className="level-override-editor__row-label">
          {t('context.viewEditor.levelOverrides.density')}
        </span>
        <SegmentedControlAtom
          mode="single"
          size="small"
          options={densityOptions}
          value={override.cardDensity ?? 'inherit'}
          onChange={(v) =>
            onFieldChange(
              'cardDensity',
              v === 'inherit' ? undefined : (v as 'minimal' | 'standard' | 'expanded')
            )
          }
          ariaLabel={t('context.viewEditor.levelOverrides.density')}
        />
      </div>

      {flagRow('showStatus', t('context.viewEditor.levelOverrides.showStatus'))}
      {flagRow('showFlow', t('context.viewEditor.levelOverrides.showFlow'))}
      {flagRow('showAvatar', t('context.viewEditor.levelOverrides.showAvatar'))}

      <div className="level-override-editor__row level-override-editor__row--properties">
        <span className="level-override-editor__row-label">
          {t('context.viewEditor.levelOverrides.visibleProperties')}
        </span>
        <div className="level-override-editor__properties">
          <SegmentedControlAtom
            mode="single"
            size="small"
            options={propertiesModeOptions}
            value={hasCustomProperties ? 'custom' : 'inherit'}
            onChange={(v) =>
              onFieldChange('visibleProperties', v === 'custom' ? [] : undefined)
            }
            ariaLabel={t('context.viewEditor.levelOverrides.visibleProperties')}
          />
          {hasCustomProperties && (
            <div className="level-override-editor__chips">
              {(visibleProperties ?? []).map((property) => (
                <span key={property} className="level-override-editor__prop-chip">
                  <span className="level-override-editor__prop-chip-label">{property}</span>
                  {!disabled && (
                    <button
                      type="button"
                      className="level-override-editor__prop-chip-remove"
                      onClick={() =>
                        onFieldChange(
                          'visibleProperties',
                          (visibleProperties ?? []).filter((p) => p !== property)
                        )
                      }
                      aria-label={t('context.viewEditor.levelOverrides.removePropertyAriaLabel', { property })}
                    >
                      <i className="pi pi-times" />
                    </button>
                  )}
                </span>
              ))}
              {!disabled && (
                addingProperty ? (
                  <span className="level-override-editor__add-property">
                    {remainingPropertyOptions.length > 0 ? (
                      <DropdownAtom
                        value={null}
                        options={remainingPropertyOptions}
                        onChange={(e) => {
                          if (e.value) {
                            onFieldChange('visibleProperties', [...(visibleProperties ?? []), e.value]);
                          }
                          setAddingProperty(false);
                        }}
                        placeholder={t('context.viewEditor.levelOverrides.selectProperties')}
                        className="level-override-editor__input"
                      />
                    ) : (
                      <InputAtom
                        value={newProperty}
                        autoFocus
                        onChange={(e) => setNewProperty(e.target.value)}
                        onBlur={commitNewProperty}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitNewProperty();
                          if (e.key === 'Escape') {
                            setNewProperty('');
                            setAddingProperty(false);
                          }
                        }}
                        placeholder={t('context.viewEditor.levelOverrides.propertyNamePlaceholder')}
                        className="level-override-editor__input"
                      />
                    )}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="level-override-editor__add-chip"
                    onClick={() => setAddingProperty(true)}
                  >
                    <i className="pi pi-plus" /> {t('context.viewEditor.levelOverrides.addProperty')}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LevelOverrideEditorMolecule;
